class FilterValue {
    constructor (id, title) {
        this.id = id
        this.title = title
    }
}

export const TYPES = {
    CHECKBOX: 'checkbox',
    SELECT: 'select',
    RADIO: 'radio',
    SWITCH: 'switch',
    RANGE: 'range',
}
class Filter {
    constructor (id, type, title) {
        this.id = id
        this.title = title
        this.type = type

        /** @var {Map<string, FilterValue>} */
        this._values = new Map()
    }
    
    clear() {}
    
    /**
     * @param {any} item
     * @param {string} key
     * @return {void}
     */
    apply( item, key ) {}

    /**
     * @param {Array} values
     */
    with( values ) {
        const self = this
        return {
            satisfies ( itemKey ) {
                return false
            }
        }
    }
    
    get values() {
        return Array.from(this._values.values())
    }

    getApplied(items) {
        return {
            id: this.id,
            type: this.type,
            title: this.title,
            values: this.values,
        }
    }
}

class FilterWithItems extends Filter {
    constructor (id, type, title, values = []) {
        super (id, type, title);
        this._items = new Map()

        for (const item of values) {
            this._values.set(item.id.toString(), new FilterValue(item.id.toString(), item.title))
        }
    }
    
    clear() {
        this._items.clear()
    }

    apply( item, key ) {
        for (const filterValue of this._values.values()) {
            const filterItems = this._items.get(filterValue.id) || new Set()
            if (this.satisfies(item, filterValue.id)) {
                filterItems.add(key)
                this._items.set(filterValue.id, filterItems)
            }
        }
    }

    getApplied(items) {
        const result = super.getApplied(items)
        result.values = result.values.map(filterValue => {
            let count = 0
            let minPriceSort = Infinity
            let minPrice = Infinity
            let currency = 'EUR'
            
            for (const item of items) {
                if (this.satisfies(item, filterValue.id)) {
                    count++

                    if (item.prices.price_sort && item.prices.price_sort < minPriceSort) {
                        minPrice = item.prices.price
                        minPriceSort = item.prices.price_sort
                        currency = item.prices.currency.code
                    }
                }
            }

            return {
                id: filterValue.id,
                title: filterValue.title,
                count: count,
                price: minPrice,
                currency: currency,
            }
        })
        
        return result
    }

    with( values ) {
        const self = this
        return {
            satisfies ( itemKey ) {
                return values.some(value => {
                    /** @var {Set<string>} keys */
                    const keys = self._items.get(value.toString()) || new Set()
                    return keys.has(itemKey)
                })
            }
        }
    }

    /**
     * @param {any} item
     * @param {string|number|null} value
     * @return {boolean}
     */
    satisfies( item, value ) {
        return false
    }
}

class BooleanFilter extends FilterWithItems {
    constructor (id, type, title) {
        super (id, type, title, [
            { id: '0', title: __('common.no') },
            { id: '1', title: __('request.facilities.yes') },
        ]);
    }
}

class ClassFilter extends FilterWithItems {
    satisfies ( item, value) {
        return item.aircraft.class?.id?.toString() === value.toString()
    }
}

class ManufacturerFilter extends FilterWithItems {
    satisfies ( item, value) {
        return item.aircraft.manufacturer?.id?.toString() === value.toString()
    }
}

class PriceFilter extends Filter {
    constructor (id, type, title, fromTitle, toTitle) {
        super (id, type, title);
        this.fromTitle = fromTitle
        this.toTitle = toTitle

        this._items = []
    }
    
    apply( item, key ) {
        this._items.push({ key, value: item.prices.price_sort })
    }

    with( values ) {
        const self = this
        return {
            satisfies ( itemKey ) {
                if (values.length === 0) return true
                
                const { from, to } = values[0]
                const fromNum = from !== undefined && from !== null && from !== '' ? Number(from) : null
                const toNum = to !== undefined && to !== null && to !== '' ? Number(to) : null
                const hasFrom = fromNum !== null && !Number.isNaN(fromNum)
                const hasTo = toNum !== null && !Number.isNaN(toNum)
                const items = self._items
                    .filter ( ( { value } ) => {
                        if ( hasFrom && hasTo ) {
                            if ( toNum < fromNum ) {
                                return value >= fromNum
                            }
                            return value >= fromNum && value <= toNum
                        } else if ( hasFrom ) {
                            return value >= fromNum
                        } else if ( hasTo ) {
                            return value <= toNum
                        } else {
                            return true
                        }
                    } )
                    .map(({ key }) => key)
                
                return items.includes(itemKey)
            }
        }
    }

    getApplied(items) {
        const result = super.getApplied(items)
        result.fromTitle = this.fromTitle
        result.toTitle = this.toTitle

        return result
    }
}

class YearFilter extends FilterWithItems {
    satisfies ( item, value) {
        if (!value) return true
        return parseInt(item.aircraft.year?.toString()) >= parseInt(value.toString())
    }
}

class HeightFilter extends FilterWithItems {
    satisfies ( item, value) {
        return item.aircraft.height >= parseFloat(value.toString())
    }
}

class StewardessFilter extends BooleanFilter {
    satisfies ( item, value) {
        return item.aircraft.stewardess?.toString() === value.toString()
    }
}

class FoodFilter extends FilterWithItems {
    satisfies ( item, value) {
        return item.aircraft.food?.toString() === value.toString()
    }
}

class IsEmptylegFilter extends BooleanFilter {
    satisfies ( item, value) {
        return item.is_emptyleg === true
    }
}

class FilterFactory {
    static create(filterDto) {
        switch ( filterDto.id ) {
            case 'class': return new ClassFilter(filterDto.id, filterDto.type, filterDto.title, filterDto.items)
            case 'manufacturer': return new ManufacturerFilter(filterDto.id, filterDto.type, filterDto.title, filterDto.items)
            case 'price': return new PriceFilter(filterDto.id, filterDto.type, filterDto.title, filterDto.from, filterDto.to)
            case 'year': return new YearFilter(filterDto.id, filterDto.type, filterDto.title, filterDto.items)
            case 'height': return new HeightFilter(filterDto.id, filterDto.type, filterDto.title, filterDto.items)
            case 'stewardess': return new StewardessFilter(filterDto.id, filterDto.type, filterDto.title)
            case 'food': return new FoodFilter(filterDto.id, filterDto.type, filterDto.title, filterDto.items)
            case 'is_emptyleg': return new IsEmptylegFilter(filterDto.id, filterDto.type, filterDto.title)
            default: return null
        }
    }
}

export function useSearchFilters({ 
    enableIsEmptylegFilter = false,
    getItemKey = e => e.id.toString(), 
}) {
    const [recounting, setRecounting] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [filters, setFilters] = React.useState([]);
    const [items, setItems] = React.useState([]);
    const recountingRef = React.useRef(null);
    
    const loadFilters = React.useCallback(async (signal = undefined) => {
        setLoading(true);
        setError(null);
        
        try {
            const filters = await searchAction.fetchSearchFilters();
            if (signal?.aborted) {
                return
            }

            const newFilters = filters
                .map(item => FilterFactory.create(item))
                .filter(Boolean)

            if (enableIsEmptylegFilter) {
                newFilters.unshift(FilterFactory.create({ id: 'is_emptyleg', type: 'switch', title: __('search.filter.emptylegsOnly') }))
            }
            
            setFilters(newFilters)
        } catch ( error ) {
            console.error(error)
            setError( __('search.filter.unavailable') );
        } finally {
            setLoading(false);
        }
    }, [])
    
    const recount = () => {
        setRecounting(true)
        if (recountingRef.current)
            clearTimeout(recountingRef.current)
        
        recountingRef.current = setTimeout(() => {
            for (const filter of filters) {
                filter.clear()
            }

            for (const item of items) {
                for (const filter of filters) {
                    filter.apply( item, getItemKey(item) )
                }
            }
            setRecounting(false)
        })
    };
    
    const recountFilters = (items) => {
        setItems(items)
    };
    const resetFilters = () => {
        setItems([])
    };
    const IsValue = e => typeof e !== "undefined" && e !== null && (typeof e !== 'number' || !isNaN(e))
    const filterItems = React.useCallback((items, currentFilters) => {
        return items.filter(item => {
            for (const filterKey in currentFilters) {
                const filterValues = (Array.isArray(currentFilters[filterKey]) ? currentFilters[filterKey] : [currentFilters[filterKey]])
                    .filter(IsValue)
                
                if (filterValues.length === 0)
                    continue
                
                const filter = filters.find(e => e.id === filterKey)
                if (!filter)
                    continue

                if (!filter.with(filterValues).satisfies(getItemKey(item))) {
                    return false
                }
            }
            
            return true
        })
    }, [ getItemKey ])
    
    const filterFilters = React.useCallback((values = {}) => {
        return filters.map(filter => {
            const valuesWithoutCurrent = {...values, [filter.id]: undefined}
            const filteredItems = filterItems(items, valuesWithoutCurrent)
            
            return filter.getApplied(filteredItems)
        })
    }, [ items, filters ])

    React.useEffect(() => {
        loadFilters()
    }, [])
    
    React.useEffect(() => {
        recount()
    }, [ items, filters ])
    
    return {
        // functions
        loadFilters,
        recountFilters,
        resetFilters,
        filterFilters,
        filterItems,

        // state
        loading,
        recounting,
        error,
    }
}

export function withSearchFiltersHook(Component, { enableIsEmptylegFilter = false }) {
    return function ( props ) {
        const searchFilters = useSearchFilters({ enableIsEmptylegFilter });
        return <Component {...props} searchFilters={searchFilters} />
    }
}
